// PersonDetailPanel.jsx – Seitenleiste mit den Angaben der angeklickten Person:
// Foto, Name, Geburtsdatum, Verwandte und Knöpfe (Bild bearbeiten, Bearbeiten, Löschen).
// Ist ein Knopf-Prop null (z.B. onDelete im Nur-Anschauen-Modus), wird der Knopf nicht gezeigt.
// familytree/src/components/PersonDetailPanel.jsx
import React from 'react';
import { EditedPhoto } from './EditedPhoto';
import './PersonDetailPanel.css';

export function PersonDetailPanel({ person, persons, relationships, onEdit, onDelete, onClose, onDownloadPhoto, onEditPhoto, onAddPhoto }) {
  if (!person) return null;

  // Verwandte aus der Beziehungsliste heraussuchen: Eltern, Kinder, Partner, Ex-Partner.
  const parents = relationships
    .filter(r => r.type === 'parent-child' && r.to === person.id)
    .map(r => persons.find(p => p.id === r.from))
    .filter(Boolean);

  const children = relationships
    .filter(r => r.type === 'parent-child' && r.from === person.id)
    .map(r => persons.find(p => p.id === r.to))
    .filter(Boolean);

  const partners = relationships
    .filter(r => r.type === 'partner' && (r.from === person.id || r.to === person.id))
    .map(r => persons.find(p => p.id === (r.from === person.id ? r.to : r.from)))
    .filter(Boolean);

  const exPartners = relationships
    .filter(r => r.type === 'ex-partner' && (r.from === person.id || r.to === person.id))
    .map(r => persons.find(p => p.id === (r.from === person.id ? r.to : r.from)))
    .filter(Boolean);

  // Siblings: share at least one parent
  // Geschwister = Personen mit mindestens einem gemeinsamen Elternteil.
  const siblings = persons.filter(p => {
    if (p.id === person.id) return false;
    const myParentIds = parents.map(par => par.id);
    if (myParentIds.length === 0) return false;
    const theirParentIds = relationships
      .filter(r => r.type === 'parent-child' && r.to === p.id)
      .map(r => r.from);
    return theirParentIds.some(id => myParentIds.includes(id));
  });

  // „1980-05-17“ → „17. Mai 1980“.
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const genderLabel = person.gender === 'male' ? 'Männlich' : person.gender === 'female' ? 'Weiblich' : 'Unbekannt';

  // Kleine Hilfskomponente: Überschrift + Liste von Namen (z.B. „Kinder: Anna, Ben“).
  const RelList = ({ items, label }) => {
    if (items.length === 0) return null;
    return (
      <div className="rel-group">
        <span className="rel-label">{label}</span>
        <div className="rel-items">
          {items.map(p => (
            <span key={p.id} className="rel-chip">
              {p.name || '(Kein Name)'}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="detail-panel">
      <button className="panel-close" onClick={onClose}>×</button>

      <div className="panel-photo">
        {person.photo ? (
          <EditedPhoto photo={person.photo} edit={person.photoEdit} alt={person.name} />
        ) : (
          <div className={`panel-photo-placeholder ${person.gender === 'male' ? 'male' : person.gender === 'female' ? 'female' : 'neutral'}`}>
            {person.name ? person.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?'}
          </div>
        )}
      </div>
      {person.photo && person.photoRetouch && (
        <div title={person.photoRetouch.note || undefined}
          style={{ textAlign: 'center', fontSize: 12, color: '#6b21a8', margin: '-4px 0 6px', whiteSpace: 'pre-wrap' }}>
          ✋ Wird von Hand bearbeitet{person.photoRetouch.note ? `: ${person.photoRetouch.note}` : ''}
        </div>
      )}

      <h2 className="panel-name">{person.name || '(Kein Name)'}</h2>
      {person.maidenName && (
        <p className="panel-maiden">geb. {person.maidenName}</p>
      )}

      <div className="panel-meta">
        {person.birthDate && (
          <div className="meta-item">
            <span className="meta-icon">🎂</span>
            <span>{formatDate(person.birthDate)}</span>
          </div>
        )}
        <div className="meta-item">
          <span className="meta-icon">{person.gender === 'male' ? '♂' : person.gender === 'female' ? '♀' : '○'}</span>
          <span>{genderLabel}</span>
        </div>
      </div>

      <div className="panel-relations">
        <RelList items={parents} label="Eltern" />
        <RelList items={partners} label="Partner" />
        <RelList items={exPartners} label="Expartner" />
        <RelList items={siblings} label="Geschwister" />
        <RelList items={children} label="Kinder" />
      </div>

      <div className="panel-actions">
        {person.photo && onEditPhoto && (
          <button className="btn btn--ghost panel-btn panel-btn--wide" onClick={() => onEditPhoto(person)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 2v14a2 2 0 0 0 2 2h14"/><path d="M18 22V8a2 2 0 0 0-2-2H2"/>
            </svg>
            Bild bearbeiten
          </button>
        )}
        {!person.photo && onAddPhoto && (
          <button className="btn btn--ghost panel-btn panel-btn--wide" onClick={() => onAddPhoto(person)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="5" width="18" height="14" rx="2"/><circle cx="12" cy="12" r="3"/>
            </svg>
            Foto hinzufügen
          </button>
        )}
        {onDownloadPhoto && (
          <button className="btn btn--ghost btn--download panel-btn panel-btn--wide" onClick={() => onDownloadPhoto(person)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Foto laden
          </button>
        )}
        {onEdit && <button className="btn btn--ghost panel-btn" onClick={() => onEdit(person.id)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
          Bearbeiten
        </button>}
        {onDelete && <button className="btn btn--danger panel-btn" onClick={() => {
          if (window.confirm(`"${person.name}" wirklich löschen?`)) {
            onDelete(person.id);
            onClose();
          }
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-1 14H6L5 6"/>
            <path d="M10 11v6M14 11v6"/>
            <path d="M9 6V4h6v2"/>
          </svg>
          Löschen
        </button>}
      </div>
    </div>
  );
}
